import { useState, useEffect } from 'react';
import { genId, validateUrl, postApi } from './ProxyUtils';
import { useInlineEdit } from './useInlineEdit';
import { useGlobalListLogic } from './useGlobalListLogic';
import { useVmAddresses } from './useVmAddresses';

// Helper per mappare items/groups dal formato DB al formato UI
const mapItems = (items) => (items || []).map(item => ({
    id: genId('item'), url: item.url, type: 'url',
    mongoId: item._id, createdAt: item.createdAt || new Date().toISOString()
}));

const mapGroups = (groups) => (groups || []).map(group => ({
    id: genId('group'), url: group.urls, type: 'group',
    groupName: group.groupName, groupId: group.groupId,
    mongoId: group._id, createdAt: group.createdAt || new Date().toISOString()
}));

const extractSelectedGroups = (groups) => (groups || []).map(group => ({
    idProxyGroup: group.groupId,
    name: group.groupName,
    description: group.description || '',
    urls: group.urls
}));

// Factory per creare handler di aggiunta URL a una lista (whitelist o blacklist)
const createAddUrlHandler = (listGetter, listSetter, listName, setStatus) => (urlValue, onSuccess) => {
    const trimmed = urlValue.trim();
    if (!trimmed) return setStatus({ type: 'error', message: "L'URL non può essere vuoto" });
    if (!validateUrl(trimmed)) return setStatus({ type: 'error', message: 'URL non valido' });
    if (listGetter().some(i => i.url === trimmed && i.type === 'url')) {
        return setStatus({ type: 'error', message: `URL già presente nella ${listName}` });
    }

    const newItem = { id: genId('url'), url: trimmed, type: 'url', createdAt: new Date().toISOString() };
    listSetter(prev => [...prev, newItem]);
    setStatus({ type: 'success', message: `URL aggiunto alla ${listName}` });
    if (onSuccess) onSuccess();
};

// Factory per creare handler di aggiunta gruppo a una lista
const createAddGroupHandler = (listGetter, listSetter, selectedSetter, listName, setStatus) => (group) => {
    if (!group || !group.idProxyGroup) return;

    if (listGetter().some(w => w.type === 'group' && w.groupId === group.idProxyGroup)) {
        setStatus({ type: 'info', message: `Il gruppo è già presente nella ${listName}` });
        return;
    }

    const newGroupItem = {
        id: group.idProxyGroup,
        url: group.urls || [],
        type: 'group',
        groupName: group.name,
        groupId: group.idProxyGroup,
        createdAt: new Date().toISOString()
    };
    listSetter(prev => [...prev, newGroupItem]);
    selectedSetter(prev => {
        const exists = prev.some(g => g.idProxyGroup === group.idProxyGroup);
        return exists ? prev : [...prev, group];
    });
    setStatus({ type: 'success', message: `Gruppo aggiunto alla ${listName}` });
};

// Factory per handler di edit inline su liste locali
const createEditSaveHandler = (listGetter, listSetter, editHook, setStatus) => (id) => {
    const trimmed = editHook.editValue.trim();
    if (!trimmed) return setStatus({ type: 'error', message: "L'URL non può essere vuoto" });
    if (!validateUrl(trimmed)) return setStatus({ type: 'error', message: 'URL non valido' });
    if (listGetter().some(item => item.url === trimmed && item.id !== id)) {
        return setStatus({ type: 'error', message: 'URL già presente' });
    }

    listSetter(prev => prev.map(item => item.id === id ? { ...item, url: trimmed } : item));
    editHook.cancel();
    setStatus({ type: 'success', message: 'URL modificato con successo' });
};

// Helper per preparare payload lista per il salvataggio
const prepareListPayload = (urls) => {
    const items = urls
        .filter(item => item.type === 'url')
        .map(item => ({ url: item.url, type: 'url' }));

    const groups = urls
        .filter(item => item.type === 'group')
        .map(item => ({
            urls: Array.isArray(item.url) ? item.url : [item.url],
            type: 'group',
            groupId: item.groupId,
            groupName: item.groupName
        }));

    return { items, groups };
};

export const useProxyLogic = (labProps, apiAuthResponse) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState(null);
    const [whitelistUrls, setWhitelistUrls] = useState([]);
    const [blacklistUrls, setBlacklistUrls] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Gruppi
    const [availableGroups, setAvailableGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedBlacklistGroups, setSelectedBlacklistGroups] = useState([]);

    // Inline editing (whitelist e blacklist locali)
    const whitelistEdit = useInlineEdit();
    const blacklistEdit = useInlineEdit();

    // Logica liste globali (hook separato)
    const globalLogic = useGlobalListLogic(setStatus);

    // VM addresses
    const vmAddresses = useVmAddresses(labProps?.idLab, labProps?.labType);

    // Stato attuale dal servizio proxy
    const [proxyServiceInfo, setProxyServiceInfo] = useState(null);
    const [loadingProxyInfo, setLoadingProxyInfo] = useState(false);

    // --- Carica dati proxy dal database ---
    const processProxyData = (proxyManage) => {
        try {
            setWhitelistUrls([...mapItems(proxyManage.whitelistItems), ...mapGroups(proxyManage.whitelistGroups)]);
            setSelectedGroups(extractSelectedGroups(proxyManage.whitelistGroups));
            setBlacklistUrls([...mapItems(proxyManage.blacklistItems), ...mapGroups(proxyManage.blacklistGroups)]);
            setSelectedBlacklistGroups(extractSelectedGroups(proxyManage.blacklistGroups));
        } catch (error) {
            console.error("Errore nell'elaborazione dei dati proxy:", error);
            resetProxyData();
            setStatus({ type: 'error', message: "Errore nell'elaborazione dei dati caricati" });
        }
    };

    const resetProxyData = () => {
        setWhitelistUrls([]);
        setBlacklistUrls([]);
        setSelectedGroups([]);
        setSelectedBlacklistGroups([]);
    };

    const loadProxyWhitelistFromDB = async (signal) => {
        if (!labProps?.idLab) return;
        setIsLoading(true);
        try {
            const data = await postApi('REACT_APP_URL_GET_LAB_PROXY_WHITELIST', { idLab: labProps.idLab }, {
                signal,
                mock: () => {
                    console.log("DEBUG: Caricamento whitelist e blacklist proxy (MOCK)");
                    processProxyData({
                        whitelistItems: [
                            { url: "example.com", type: "url", _id: "mock1" },
                            { url: "test.org", type: "url", _id: "mock2" }
                        ],
                        whitelistGroups: [{
                            urls: ["*.github.com", "*.devops.com"], type: "group",
                            groupId: 1, groupName: "github", _id: "mock3"
                        }],
                        blacklistItems: [
                            { url: "malware.com", type: "url", _id: "mock4" },
                            { url: "phishing.net", type: "url", _id: "mock5" }
                        ],
                        blacklistGroups: [{
                            urls: ["*.darknet.com", "*.blocked.com"], type: "group",
                            groupId: 10, groupName: "blocked-sites", _id: "mock6"
                        }]
                    });
                    return null;
                }
            });

            if (data?.data) {
                if (data.data.proxyManage) {
                    processProxyData(data.data.proxyManage);
                    setStatus({ type: 'success', message: 'Whitelist e blacklist proxy caricate dal database' });
                } else {
                    resetProxyData();
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error("Errore nel caricamento whitelist e blacklist proxy:", err);
            setStatus({ type: 'error', message: 'Errore nel caricamento della whitelist e blacklist proxy dal database' });

            // Fallback ai dati dalle props
            if (labProps?.proxyWhitelist) {
                const urls = labProps.proxyWhitelist.map((item) => ({
                    id: genId('item'),
                    url: typeof item === 'string' ? item : item.url,
                    type: typeof item === 'string' ? 'url' : (item.type || 'url'),
                    groupName: typeof item === 'object' ? item.groupName : null,
                    groupId: typeof item === 'object' ? item.groupId : null,
                    createdAt: item.createdAt || new Date().toISOString()
                }));
                setWhitelistUrls(urls);
                const groups = urls
                    .filter(i => i.type === 'group' && i.groupId != null)
                    .map(i => ({ idProxyGroup: i.groupId, name: i.groupName }));
                setSelectedGroups(groups);
            } else {
                resetProxyData();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // --- Caricamento gruppi disponibili ---
    const loadAvailableGroups = async (signal) => {
        setLoadingGroups(true);
        try {
            const data = await postApi('REACT_APP_URL_GET_WHITELIST_GROUPS', {}, {
                signal,
                mock: () => {
                    setAvailableGroups([
                        { idProxyGroup: 1, name: 'Social Media', description: 'Principali piattaforme social', urls: ['*.facebook.com', '*.twitter.com', '*.instagram.com', '*.linkedin.com'] },
                        { idProxyGroup: 2, name: 'Google Services', description: 'Servizi Google essenziali', urls: ['*.google.com', '*.googleapis.com', '*.gstatic.com'] },
                        { idProxyGroup: 3, name: 'Microsoft Services', description: 'Servizi Microsoft Office 365', urls: ['*.microsoft.com', '*.office.com', '*.microsoftonline.com'] }
                    ]);
                    return null;
                }
            });

            if (data) {
                setAvailableGroups(data.data || []);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error(err);
            setStatus({ type: 'error', message: 'Errore nel caricamento dei gruppi whitelist' });
        } finally {
            setLoadingGroups(false);
        }
    };

    // --- Carica stato attuale dal servizio proxy ---
    const loadProxyServiceInfo = async (signal) => {
        if (!labProps?.idLab) return;
        setLoadingProxyInfo(true);
        try {
            const data = await postApi('REACT_APP_URL_GET_PROXY_INFO', { idLab: labProps.idLab }, {
                signal,
                mock: () => {
                    console.log("DEBUG: Caricamento proxy info (MOCK)");
                    setProxyServiceInfo({
                        name: `${labProps.idLab}`,
                        allowed_sites: ["example.com"],
                        blocked_sites: ["malware.com"],
                        addresses: ["10.0.1.10"],
                        docenti: ["docente@unimib.it"]
                    });
                    return null;
                }
            });
            if (data?.data) {
                setProxyServiceInfo(data.data.result || data.data);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error("Errore nel caricamento proxy info:", err);
        } finally {
            setLoadingProxyInfo(false);
        }
    };

    // --- Inizializzazione ---
    useEffect(() => {
        const abortController = new AbortController();
        loadProxyWhitelistFromDB(abortController.signal);
        loadAvailableGroups(abortController.signal);
        globalLogic.loadGlobalLists(abortController.signal);
        vmAddresses.loadVmList(abortController.signal);
        loadProxyServiceInfo(abortController.signal);
        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labProps?.idLab]);

    // Timer per pulire i messaggi di stato
    useEffect(() => {
        if (!status) return;
        const t = setTimeout(() => setStatus(null), 5000);
        return () => clearTimeout(t);
    }, [status]);

    // --- Handlers whitelist locale ---
    const handleAddUrl = createAddUrlHandler(
        () => whitelistUrls, setWhitelistUrls, 'whitelist', setStatus
    );

    const handleAddSingleGroup = createAddGroupHandler(
        () => whitelistUrls, setWhitelistUrls, setSelectedGroups, 'whitelist', setStatus
    );

    const handleDeleteItem = (id) => {
        const item = whitelistUrls.find(i => i.id === id);
        if (!item) return;
        if (item.type === 'group' && item.groupId != null) {
            setSelectedGroups(prev => prev.filter(g => g.idProxyGroup !== item.groupId));
        }
        setWhitelistUrls(prev => prev.filter(i => i.id !== id));
        setStatus({ type: 'success', message: 'Elemento rimosso dalla whitelist' });
    };

    const handleEditStart = (id, currentUrl) => whitelistEdit.start(id, currentUrl);
    const handleEditCancel = () => whitelistEdit.cancel();
    const handleEditSave = createEditSaveHandler(
        () => whitelistUrls, setWhitelistUrls, whitelistEdit, setStatus
    );

    // --- Handlers blacklist locale ---
    const handleAddBlacklistUrl = createAddUrlHandler(
        () => blacklistUrls, setBlacklistUrls, 'blacklist', setStatus
    );

    const handleAddSingleBlacklistGroup = createAddGroupHandler(
        () => blacklistUrls, setBlacklistUrls, setSelectedBlacklistGroups, 'blacklist', setStatus
    );

    const handleBlacklistEditStart = (id, currentUrl) => blacklistEdit.start(id, currentUrl);
    const handleBlacklistEditCancel = () => blacklistEdit.cancel();
    const handleBlacklistEditSave = createEditSaveHandler(
        () => blacklistUrls, setBlacklistUrls, blacklistEdit, setStatus
    );

    const handleBlacklistDeleteItem = async (itemId) => {
        const item = blacklistUrls.find(i => i.id === itemId);
        if (!item) return;
        const confirmed = window.confirm(
            `Sei sicuro di voler eliminare "${item.type === 'group' ? item.groupName : item.url}" dalla blacklist?`
        );
        if (!confirmed) return;
        setBlacklistUrls(prev => prev.filter(i => i.id !== itemId));
        if (item.type === 'group') {
            setSelectedBlacklistGroups(prev => prev.filter(g => g.idProxyGroup !== item.groupId));
        }
        setStatus({ type: 'success', message: 'Elemento rimosso dalla blacklist' });
    };

    // --- Gestione gruppi (CRUD) ---
    const handleCreateNewGroup = async (groupPayload) => {
        const mockFn = async () => {
            await new Promise(r => setTimeout(r, 700));
            const newGroup = { idProxyGroup: Date.now(), name: groupPayload.name, description: groupPayload.description, urls: groupPayload.urls };
            setAvailableGroups(prev => [...prev, newGroup]);
            setStatus({ type: 'success', message: 'Nuovo gruppo creato con successo (simulato)' });
            return null;
        };

        const data = await postApi('REACT_APP_URL_CREATE_WHITELIST_GROUP', {
            name: groupPayload.name,
            description: groupPayload.description,
            urls: groupPayload.urls,
        }, { mock: mockFn });

        if (data) {
            setStatus({ type: 'success', message: 'Nuovo gruppo creato con successo' });
            loadAvailableGroups();
        }
    };

    const updateGroupInLists = (groupId, groupPayload) => {
        setWhitelistUrls(prev => prev.map(item =>
            item.type === 'group' && item.groupId === groupId
                ? { ...item, groupName: groupPayload.name, url: groupPayload.urls }
                : item
        ));
        setSelectedGroups(prev => prev.map(g =>
            g.idProxyGroup === groupId
                ? { ...g, name: groupPayload.name, description: groupPayload.description }
                : g
        ));
    };

    const handleEditGroup = async (groupId, groupPayload) => {
        const mockFn = async () => {
            await new Promise(r => setTimeout(r, 700));
            setAvailableGroups(prev => prev.map(g =>
                g.idProxyGroup === groupId
                    ? { ...g, name: groupPayload.name, description: groupPayload.description, urls: groupPayload.urls }
                    : g
            ));
            updateGroupInLists(groupId, groupPayload);
            setStatus({ type: 'success', message: 'Gruppo modificato con successo (simulato)' });
            return null;
        };

        const data = await postApi('REACT_APP_URL_UPDATE_WHITELIST_GROUP', {
            idProxyGroup: groupId,
            name: groupPayload.name,
            description: groupPayload.description,
            urls: groupPayload.urls,
        }, { mock: mockFn });

        if (data) {
            setStatus({ type: 'success', message: 'Gruppo modificato con successo' });
            loadAvailableGroups();
            updateGroupInLists(groupId, groupPayload);
        }
    };

    const removeGroupFromLists = (groupId) => {
        setWhitelistUrls(prev => prev.filter(item => !(item.type === 'group' && item.groupId === groupId)));
        setSelectedGroups(prev => prev.filter(g => g.idProxyGroup !== groupId));
    };

    const handleDeleteGroup = async (groupId) => {
        const mockFn = async () => {
            await new Promise(r => setTimeout(r, 700));
            setAvailableGroups(prev => prev.filter(g => g.idProxyGroup !== groupId));
            removeGroupFromLists(groupId);
            setStatus({ type: 'success', message: 'Gruppo eliminato con successo (simulato)' });
            return null;
        };

        const data = await postApi('REACT_APP_URL_DELETE_WHITELIST_GROUP', { idProxyGroup: groupId }, { mock: mockFn });

        if (data) {
            setStatus({ type: 'success', message: 'Gruppo eliminato con successo' });
            loadAvailableGroups();
            removeGroupFromLists(groupId);
        }
    };

    // --- Salvataggio regole (whitelist + blacklist) ---
    const handleSaveRules = async () => {
        if (!labProps?.idLab) {
            setStatus({ type: 'error', message: 'ID laboratorio non trovato' });
            return;
        }

        setIsSubmitting(true);
        setIsLoading(true);
        try {
            const wl = prepareListPayload(whitelistUrls);
            const bl = prepareListPayload(blacklistUrls);

            const payload = {
                idLab: labProps.idLab,
                whitelistItems: wl.items,
                whitelistGroups: wl.groups,
                blacklistItems: bl.items,
                blacklistGroups: bl.groups,
                docenti: (labProps.teachers || []).map(t => t.email).filter(Boolean)
            };

            const data = await postApi('REACT_APP_URL_UPDATE_PROXY_WHITELIST', payload, {
                mock: async () => {
                    await new Promise(r => setTimeout(r, 700));
                    return { statusRequest: 'success' };
                }
            });

            if (data) {
                // Invia gli IP delle VM accese al backend per il proxy
                if (vmAddresses.vmIpAddresses.length > 0) {
                    try {
                        await vmAddresses.sendAddresses();
                    } catch (addrErr) {
                        console.error("Errore nell'invio degli indirizzi VM:", addrErr);
                        setStatus({ type: 'warning', message: 'Regole proxy aggiornate, ma errore nell\'invio degli indirizzi VM al proxy' });
                        return;
                    }
                    setStatus({ type: 'success', message: 'Regole proxy e indirizzi VM aggiornati con successo' });
                } else {
                    setStatus({ type: 'success', message: 'Regole proxy aggiornate con successo (nessuna VM accesa da notificare)' });
                }
            }
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.message || 'Errore di connessione' });
        } finally {
            setIsSubmitting(false);
            setIsLoading(false);
        }
    };

    // handleSaveWhitelist mantenuto per backward compat (alias di handleSaveRules)
    const handleSaveWhitelist = handleSaveRules;

    return {
        // State
        whitelistUrls,
        blacklistUrls,
        availableGroups,
        selectedGroups,
        selectedBlacklistGroups,
        status,
        isSubmitting: isSubmitting || globalLogic.isSubmittingGlobal,
        isLoading,
        loadingGroups,
        loadingGlobalLists: globalLogic.loadingGlobalLists,
        editingUrl: whitelistEdit.editingId,
        editUrlValue: whitelistEdit.editValue,
        editingBlacklistUrl: blacklistEdit.editingId,
        editBlacklistUrlValue: blacklistEdit.editValue,
        // Global Lists State
        globalWhitelist: globalLogic.globalWhitelist,
        editingGlobalWhitelistId: globalLogic.editingGlobalWhitelistId,
        editGlobalWhitelistValue: globalLogic.editGlobalWhitelistValue,
        // VM Addresses State
        vmList: vmAddresses.vmList,
        vmIpAddresses: vmAddresses.vmIpAddresses,
        loadingVm: vmAddresses.loadingVm,
        vmError: vmAddresses.vmError,
        loadVmList: vmAddresses.loadVmList,
        // Proxy Service Info State
        proxyServiceInfo,
        loadingProxyInfo,
        loadProxyServiceInfo,
        // Actions
        setStatus,
        setEditUrlValue: whitelistEdit.setEditValue,
        setEditBlacklistUrlValue: blacklistEdit.setEditValue,
        handleAddUrl,
        handleAddBlacklistUrl,
        handleAddSingleGroup,
        handleAddSingleBlacklistGroup,
        handleDeleteItem,
        handleBlacklistDeleteItem,
        handleEditStart,
        handleEditCancel,
        handleEditSave,
        handleBlacklistEditStart,
        handleBlacklistEditCancel,
        handleBlacklistEditSave,
        handleCreateNewGroup,
        handleEditGroup,
        handleDeleteGroup,
        handleSaveWhitelist,
        handleSaveRules,
        loadAvailableGroups,
        // Global Lists Actions
        handleEditGlobalWhitelist: globalLogic.handleEditGlobalWhitelist,
        handleSaveGlobalWhitelistEdit: globalLogic.handleSaveGlobalWhitelistEdit,
        handleCancelGlobalWhitelistEdit: globalLogic.handleCancelGlobalWhitelistEdit,
        handleDeleteGlobalWhitelist: globalLogic.handleDeleteGlobalWhitelist,
        handleAddNewGlobalWhitelist: globalLogic.handleAddNewGlobalWhitelist,
        handleAddGlobalGroup: globalLogic.handleAddGlobalGroup,
        // Global Lists Setters
        setEditGlobalWhitelistValue: globalLogic.setEditGlobalWhitelistValue,
        setNewGlobalWhitelistUrl: globalLogic.setNewGlobalWhitelistUrl,
        newGlobalWhitelistUrl: globalLogic.newGlobalWhitelistUrl
    };
};
